/**
 * AMPD DCMS2 — session guard: one sign-in over the whole page
 *
 * When the session ends while a page is open (idle or absolute limit, sign-out in
 * another tab, revoked session), the top window shows the sign-in form ONCE, in an
 * overlay over the whole page — never inside a window (iziModal frame), a drawer or a
 * menu. After the sign-in the overlay disappears and every window learns that the
 * session is valid again:
 *
 *   - open windows keep working WITHOUT a reload — an unsaved form stays as it was,
 *   - the CSRF token (derived from the session id, which the sign-in changes) is
 *     replaced in every same-origin document: <meta name="dcms-csrf-token"> and the
 *     hidden `dcms_csrf_token` fields,
 *   - only the frames that showed "sign in" instead of their content
 *     (Login::deferSignInToTopWindow()) reload,
 *   - every document gets the `dcms:session-restored` event (detail: { csrfToken }),
 *   - other tabs of this origin get the same through BroadcastChannel `dcms2-session`.
 *
 * Who shows the overlay:
 *   - the heartbeat (sessionRefresh.js) — `not-logged` after this page has seen `logged`,
 *     confirmed by `sessionSignIn.php?state=1` (the session may still be taken over
 *     from DCMS v1),
 *   - a frame of an ended session — `window.top.DCMSSessionGuard.requireSignIn()`.
 *
 * The overlay frames `/DCMS2/sessionSignIn.php?dcms_signin_here=1` — the regular sign-in
 * form, second-factor challenge included. When signing in cannot work inside a frame
 * (an identity provider that refuses frames), "Sign in in a new tab" opens the same
 * page in a tab of its own, which reports back to this window.
 *
 * Runs in the top window only; inside a frame the module does nothing.
 *
 * @copyright AMPD Sp. z o.o. / PTWP-ONLINE Sp. z o.o.
 * @link      https://www.ampd.pl
 * @see       https://docs.ampd.pl Online documentation and examples
 */

export const SESSION_SIGN_IN_URL = '/DCMS2/sessionSignIn.php';
export const SESSION_CHANNEL = 'dcms2-session';
export const SESSION_RESTORED_EVENT = 'dcms:session-restored';
export const SESSION_EXPIRED_EVENT = 'dcms:session-expired';

const SIGN_IN_HERE_PARAM = 'dcms_signin_here';
const CSRF_META = 'dcms-csrf-token';
const CSRF_FIELD = 'dcms_csrf_token';
const OVERLAY_ID = 'dcms-session-guard';

/** Texts from the commonjs dictionary (assets/lang/{LANG}.commonjs.js), English fallback. */
function t(key, fallback) {
    const table = window.i18n_commonjs;
    return (table && table[key]) ? table[key] : fallback;
}

/**
 * Same-origin documents of a window and all its frames (depth first).
 *
 * @param {Window} win
 * @param {Window[]} [out]
 * @returns {Window[]}
 */
function sameOriginWindows(win, out = []) {
    try {
        // Throws for a frame from another origin — skipped with its subtree.
        if (!win.document) {
            return out;
        }
    } catch (e) {
        return out;
    }
    out.push(win);
    for (let i = 0; i < win.frames.length; i++) {
        sameOriginWindows(win.frames[i], out);
    }
    return out;
}

class SessionGuard {
    constructor(win) {
        this.win = win;
        this.doc = win.document;
        /** True once the heartbeat has reported a signed-in session for this page. */
        this.armed = false;
        this.overlay = null;
        this.inertElements = [];
        /** Frames that showed "sign in" instead of their content — reloaded afterwards. */
        this.waitingFrames = new Set();
        this.checking = null;

        this.channel = null;
        if (typeof BroadcastChannel === 'function') {
            try {
                this.channel = new BroadcastChannel(SESSION_CHANNEL);
                this.channel.onmessage = (event) => {
                    const data = event.data || {};
                    if (data.type === 'restored') {
                        this.applyRestored(data.csrfToken || '', false);
                    }
                };
            } catch (e) {
                this.channel = null;
            }
        }
    }

    /**
     * Heartbeat result (sessionRefresh.js).
     *
     * @param {'logged'|'not-logged'} status
     */
    heartbeat(status) {
        if (status === 'logged') {
            this.armed = true;
            if (this.overlay) {
                // Signed in elsewhere (another tab without BroadcastChannel, a normal sign-in
                // page): fetch the new CSRF token and close the overlay.
                this.checkState();
            }
            return;
        }
        if (status === 'not-logged' && this.armed && !this.overlay) {
            this.checkState();
        }
    }

    /**
     * Asks the server whether the session is valid; shows the overlay when it is not,
     * restores the page when it is.
     *
     * @returns {Promise<void>}
     */
    checkState() {
        if (this.checking) {
            return this.checking;
        }
        this.checking = fetch(SESSION_SIGN_IN_URL + '?state=1', {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((state) => {
                if (!state) {
                    return;
                }
                if (state.logged) {
                    if (this.overlay) {
                        this.restored({ csrfToken: state.csrfToken || '' });
                    }
                } else {
                    this.requireSignIn({ reason: 'heartbeat' });
                }
            })
            .catch(() => {
                // Network trouble is not a signed-out session — the next heartbeat tries again.
            })
            .finally(() => {
                this.checking = null;
            });
        return this.checking;
    }

    /**
     * Shows the sign-in overlay (once — later calls only register their frame).
     *
     * @param {{source?: Window, reason?: string}} [options]
     *        source — a frame to reload after the sign-in
     * @returns {boolean}
     */
    requireSignIn(options = {}) {
        if (options.source && options.source !== this.win) {
            this.waitingFrames.add(options.source);
        }
        if (this.overlay) {
            return true;
        }
        this.showOverlay();
        this.dispatch(SESSION_EXPIRED_EVENT, { reason: options.reason || '' });
        return true;
    }

    /**
     * The session is valid again — called by Login/sessionRestored.tpl (overlay frame or a
     * tab opened from it) and by checkState().
     *
     * @param {{csrfToken?: string}} [payload]
     */
    restored(payload = {}) {
        this.applyRestored(payload.csrfToken || '', true);
    }

    applyRestored(csrfToken, broadcast) {
        this.armed = true;
        this.hideOverlay();

        const windows = sameOriginWindows(this.win);
        for (const win of windows) {
            if (csrfToken) {
                this.replaceCsrfToken(win.document, csrfToken);
            }
        }

        // Frames that showed "sign in" instead of their content: load it now (GET of the same
        // address — a POST interrupted by the expired session is not re-sent).
        for (const frame of this.waitingFrames) {
            try {
                if (frame.location) {
                    frame.location.replace(frame.location.href);
                }
            } catch (e) {
                // Frame closed in the meantime.
            }
        }
        this.waitingFrames.clear();

        this.dispatch(SESSION_RESTORED_EVENT, { csrfToken }, windows);

        if (broadcast && this.channel) {
            try {
                this.channel.postMessage({ type: 'restored', csrfToken });
            } catch (e) {
                // Channel closed.
            }
        }
    }

    replaceCsrfToken(doc, token) {
        const meta = doc.querySelector('meta[name="' + CSRF_META + '"]');
        if (meta) {
            meta.setAttribute('content', token);
        }
        doc.querySelectorAll('input[name="' + CSRF_FIELD + '"]').forEach((input) => {
            input.value = token;
        });
    }

    dispatch(type, detail, windows = sameOriginWindows(this.win)) {
        for (const win of windows) {
            try {
                win.dispatchEvent(new win.CustomEvent(type, { detail }));
            } catch (e) {
                // Window gone.
            }
        }
    }

    signInUrl(here) {
        return SESSION_SIGN_IN_URL + (here ? '?' + SIGN_IN_HERE_PARAM + '=1' : '');
    }

    showOverlay() {
        const doc = this.doc;
        const overlay = doc.createElement('div');
        overlay.id = OVERLAY_ID;
        overlay.className = 'fixed inset-0 z-[2147483000] flex items-center justify-center bg-neutral-950/60 p-2 sm:p-4';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', OVERLAY_ID + '-title');

        const panel = doc.createElement('div');
        panel.className = 'flex w-full max-w-xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-neutral-800';

        const header = doc.createElement('div');
        header.className = 'border-b border-neutral-200 px-4 py-3 dark:border-neutral-700';
        const title = doc.createElement('h2');
        title.id = OVERLAY_ID + '-title';
        title.className = 'text-base font-semibold text-neutral-900 dark:text-neutral-50';
        title.textContent = t('sessionGuardTitle', 'Your session has expired');
        const text = doc.createElement('p');
        text.className = 'mt-1 text-sm text-neutral-600 dark:text-neutral-300';
        text.textContent = t('sessionGuardText', 'Sign in again to get back to work. Open windows and unsaved changes stay where they are.');
        header.append(title, text);

        // The form loads only on click. A form rendered long before it is used fails with
        // a stale token, and every rendered form overwrites the sign-in token cookie — with
        // overlays in several tabs only the last rendered form would sign in.
        const start = doc.createElement('div');
        start.className = 'flex justify-center px-4 py-6';
        const signIn = doc.createElement('button');
        signIn.type = 'button';
        signIn.className = 'rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400';
        signIn.textContent = t('sessionGuardSignIn', 'Sign in');
        signIn.addEventListener('click', () => {
            const frame = doc.createElement('iframe');
            frame.className = 'block w-full flex-1 border-0 bg-white dark:bg-neutral-900';
            frame.title = t('sessionGuardFrameTitle', 'Sign in');
            frame.src = this.signInUrl(true);
            panel.classList.add('h-full', 'max-h-[960px]');
            start.replaceWith(frame);
            frame.focus();
        });
        start.append(signIn);

        const footer = doc.createElement('div');
        footer.className = 'flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-4 py-2 text-sm dark:border-neutral-700';
        const hint = doc.createElement('span');
        hint.className = 'text-neutral-500 dark:text-neutral-400';
        hint.textContent = t('sessionGuardNewTabHint', 'Signing in does not work in this window?');
        const newTab = doc.createElement('button');
        newTab.type = 'button';
        newTab.className = 'font-medium text-blue-600 hover:underline dark:text-blue-400';
        newTab.textContent = t('sessionGuardNewTab', 'Sign in in a new tab');
        // Opened by script, so the tab can close itself and report back through window.opener.
        newTab.addEventListener('click', () => {
            this.win.open(this.signInUrl(false), '_blank');
        });
        footer.append(hint, newTab);

        panel.append(header, start, footer);
        overlay.append(panel);

        // The page below is unreachable while signed out — also from the keyboard.
        this.inertElements = Array.from(doc.body.children).filter((el) => !el.inert);
        this.inertElements.forEach((el) => {
            el.inert = true;
        });
        doc.body.append(overlay);
        this.overlay = overlay;
        signIn.focus();
    }

    hideOverlay() {
        if (!this.overlay) {
            return;
        }
        this.overlay.remove();
        this.overlay = null;
        this.inertElements.forEach((el) => {
            el.inert = false;
        });
        this.inertElements = [];
    }
}

/**
 * Installs the guard in the top window (once). Returns it, or null inside a frame.
 *
 * @returns {SessionGuard|null}
 */
export function initSessionGuard() {
    if (window.top !== window.self) {
        return null;
    }
    if (!window.DCMSSessionGuard) {
        window.DCMSSessionGuard = new SessionGuard(window);
    }
    return window.DCMSSessionGuard;
}
